import React, { useState, useEffect } from 'react'
import Dropdown from '../../../components/common/Dropdown'
import Table from '../../../components/common/Table/Table'
import ButtonGroup from './ButtonGroup'
import HoldValidationMessage from './HoldValidationMessage'
import { canHoldRequest } from './holdValidation'
import { APPLY_REQUEST_STATUS_HEADING } from './radioOptions'
import StatusRadioOptions from './StatusRadioOptions'
import type { DropdownOption } from '../../../components/common/Dropdown'
import { ButtonStateConfig } from './buttonStateUtils'
import {
  formatDateToMMDDYYYY,
  formatTimesDisplay,
  isISODateInPast,
} from '../../../utils/date'
import { denialReasonOptions } from './DenialReasonOptions'
import ValidationBanner from '../../../components/common/ValidationBanner'
import { scrollToTopAndFocusAlert } from '../../../utils/scrollUtils'

interface TableRow {
  id: string
  date: string
  day: string
  times: string
  requestedHours: string
  isOverlapped?: boolean
}
interface PartiallyApprovedTimeOffFormProps {
  onSubmit?: (data: {
    status: string
    approvedDates: string[]
    deniedDates: string[]
    overlappedDates?: string[]
    denialReason?: string
    comments: string
    requestId: string
  }) => void
  onCancel?: () => void
  onStatusChange?: (status: string) => void
  tableRows?: TableRow[]
  dateRequested?: string
  activeForm?: 'approved' | 'denied' | 'hold' | 'partiallyapproved' | 'returned'
  isDisabledDueToDate?: boolean
  buttonStateConfig?: ButtonStateConfig
  disableEndDate?: string
  toTypeCd?: string
  /**
   * Optional callback that receives the current approved-hours mapping and total.
   * Called on mount and whenever approvedDates changes while the form is in
   * partially approved mode. This keeps any external "Current Request" table
   * in sync without duplicating UI here.
   */
  onApprovedChange?: (payload: {
    byRow: { id: string; requestedHours: number; approvedHours: number }[]
    totalApprovedHours: number
  }) => void
  /**
   * Optional mapping provided by the container that represents the current
   * approved hours per request-day id. When present, the form will initialize
   * its checkbox state from this mapping so selections persist when the user
   * switches away from and back to the partially-approved form.
   */
}

const PartiallyApprovedTimeOffForm: React.FC<
  PartiallyApprovedTimeOffFormProps
> = ({
  onSubmit,
  onCancel,
  onStatusChange,
  tableRows = [
    {
      id: '1',
      date: '01/20/2026',
      day: 'Mon',
      times: '8:00 AM - 5:00 PM',
      requestedHours: '8.0',
    },
    {
      id: '2',
      date: '01/21/2026',
      day: 'Tue',
      times: '8:00 AM - 5:00 PM',
      requestedHours: '8.0',
    },
    {
      id: '3',
      date: '01/22/2026',
      day: 'Wed',
      times: '8:00 AM - 5:00 PM',
      requestedHours: '8.0',
    },
    {
      id: '4',
      date: '01/23/2026',
      day: 'Thu',
      times: '8:00 AM - 5:00 PM',
      requestedHours: '8.0',
    },
  ],
  dateRequested = '',
  activeForm = 'partiallyapproved',
  isDisabledDueToDate = false,
  buttonStateConfig,
  disableEndDate,
  toTypeCd,
  onApprovedChange,
}) => {
  // Calculate if Hold can be applied based on dateRequested
  const canHold = canHoldRequest(dateRequested, toTypeCd)
  const STATUS_PARTIALLY_APPROVED = 'partiallyapproved'

  const [selectedStatus, setSelectedStatus] = useState(
    STATUS_PARTIALLY_APPROVED
  )

  // normalize incoming rows so `isOverlapped` is always a boolean
  const normalizedRows = (tableRows || []).map((r) => ({
    ...r,
    isOverlapped: !!(r as any).isOverlapped,
  }))

  const [approvedDates, setApprovedDates] = useState<string[]>(
    normalizedRows.filter((r) => !r.isOverlapped).map((row) => row.id)
  )
  const [selectedDenialReason, setSelectedDenialReason] = useState<
    DropdownOption | undefined
  >()
  const [approverComments, setApproverComments] = useState('')
  const [otherDenialReason, setOtherDenialReason] = useState('')
  const [validationError, setValidationError] = useState<string | null>(null)

  /**
   * Helper: parse a string hours value (e.g. '8.0') into a number.
   * Returns 0 for invalid values so calculations stay resilient.
   */
  const parseHours = (hrs: string | undefined): number => {
    if (!hrs) return 0
    const n = Number(hrs)
    return Number.isFinite(n) ? n : 0
  }

  /**
   * Derived approved hours per row — do not mutate original tableRows.
   * When a row is unchecked, approvedHours becomes 0; otherwise it's requestedHours.
   */
  const approvedHoursByRow = tableRows.map((row) => ({
    id: row.id,
    requestedHours: parseHours(row.requestedHours),
    approvedHours: approvedDates.includes(row.id)
      ? parseHours(row.requestedHours)
      : 0,
  }))

  const totalApprovedHours = approvedHoursByRow.reduce(
    (s, r) => s + r.approvedHours,
    0
  )

  // Notify external consumers (if provided) about approved-hours changes.
  useEffect(() => {
    if (selectedStatus === STATUS_PARTIALLY_APPROVED) {
      // Emit a shallow copy to ensure callers can't mutate internal arrays
      onApprovedChange?.({
        byRow: approvedHoursByRow.map((r) => ({ ...r })),
        totalApprovedHours,
      })
    }
    // We intentionally depend on selectedStatus and approvedDates (via
    // approvedHoursByRow) so updates fire when checkboxes toggle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approvedDates, selectedStatus])

  // Whenever the status changes, always reset checkboxes to default (all
  // approved). If the user leaves partially-approved, actively notify the
  // parent so any external tables (the single Current Request table) get
  // reset immediately. When entering partially-approved we let the
  // approvedDates effect below notify the parent to avoid double emission.
  useEffect(() => {
    // Initialize/reset approvedDates to only include non-overlapped rows
    const defaultIds = tableRows.filter((r) => !(r as any).isOverlapped).map((r) => r.id)
    setApprovedDates(defaultIds)

    if (selectedStatus !== STATUS_PARTIALLY_APPROVED) {
      // Build full-approved mapping and notify parent
      const byRow = tableRows.map((r) => ({
        id: r.id,
        requestedHours: parseHours(r.requestedHours),
        approvedHours: parseHours(r.requestedHours),
      }))
      const total = byRow.reduce((s, rr) => s + rr.approvedHours, 0)
      onApprovedChange?.({ byRow, totalApprovedHours: total })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStatus, tableRows])

  // Total approved hours derived from checkbox state is computed when rendering

  // Sync selectedStatus with activeForm when it changes
  useEffect(() => {
    // Mirror whatever activeForm the parent sets so status changes from
    // outside this component reset the checkbox state via the
    // selectedStatus effect below.
    setSelectedStatus(activeForm)
  }, [activeForm])

  const handleStatusChange = (status: string): void => {
    setSelectedStatus(status)
    onStatusChange?.(status)
  }

  const handleDateToggle = (dateId: string): void => {
    setApprovedDates((prev) => {
      const isCurrentlyChecked = prev.includes(dateId)

      // If user is attempting to uncheck and this is the last checked
      // checkbox, disallow it — at least one checkbox must remain checked.
      if (
        isCurrentlyChecked &&
        prev.length <= 1 &&
        selectedStatus === STATUS_PARTIALLY_APPROVED
      ) {
        // noop: prevent unchecking the last remaining checkbox
        return prev
      }

      return isCurrentlyChecked ? prev.filter((id) => id !== dateId) : [...prev, dateId]
    })
  }
  
  const isPastDate = (date?: string) => {
    return isISODateInPast(date)
  }
  // Disable when BOTH start and end dates are before today
  const areBothDatesInPast =
    isPastDate(dateRequested) && isPastDate(disableEndDate)

const deniedDates = normalizedRows
  .filter(
    (row) =>
      !row.isOverlapped &&
      !approvedDates.includes(row.id)
  )
  .map((row) => row.id)

  const overlappedDates = normalizedRows
    .filter((row) => row.isOverlapped)
    .map((row) => row.id)

  const handleSubmit = (): void => {

     // Validate that denial reason is selected if status is denied
    if (selectedStatus === STATUS_PARTIALLY_APPROVED && !selectedDenialReason) {
      setValidationError('reasonNotSelected')
      scrollToTopAndFocusAlert()
      return
    }

    // Validate that other denial reason is filled if 'other' is selected
    if (
      selectedStatus === STATUS_PARTIALLY_APPROVED &&
      selectedDenialReason?.value === 'other' &&
      !otherDenialReason.trim()
    ) {
      setValidationError('otherReasonNotFilled')
      return
    }

     // Clear validation error if all checks pass
    setValidationError(null)
    onSubmit?.({
      status: selectedStatus,
      approvedDates,
      deniedDates,
      overlappedDates,
      denialReason: selectedDenialReason?.value === 'other'
          ? otherDenialReason
          : selectedDenialReason?.label,
      comments: approverComments,
      requestId: '1234',
    })
  }

   /**
     * Formats time strings for display.
     * Handles "Full Day", ISO timestamps, and formatted time strings.
     */
const getValidationBannerHeading = () => {
  if (validationError === 'reasonNotSelected') {
    return (
      <div>
          <div className="pt-6! pb-0! !p1 -medium text-[20px]">
            Please select a Reason for denial
          </div>
          <div className="mt-2 ml-6">
            <span className="mr-2">
              Choose a{' '}
              <a
                href="#reason-for-denial"
                className="p3 -book text-[#0074AD]! underline! pl-1!"
              >
                Reason for Denial
              </a>{' '}
              from the list to continue.
            </span>
          </div>
        </div>
    )
  }
  return null
}
  return (
    <div className="px-6! pt-0! pb-6!">
      <p className="p4 -medium text-[#0D1C3D] m-0! pb-1!">
        {APPLY_REQUEST_STATUS_HEADING.text}
      </p>
      {validationError && (
        <div className="mb-6 px-6 pt-6 col-span-2">
          <ValidationBanner
            variant="error"
            heading={getValidationBannerHeading()}
          />
        </div>
      )}

      {/* Status Radio Buttons */}
      <div className="space-y-4 mb-8 pb-6!">
        <StatusRadioOptions
          selectedStatus={selectedStatus}
          onStatusChange={handleStatusChange}
          buttonStateConfig={buttonStateConfig}
          canHold={canHold}
          isRetracted={false}
          numberOfDates={normalizedRows.length}
          disableAllOptions={isDisabledDueToDate || areBothDatesInPast}
        />
      </div>
   {/* Instruction Text */}
      {/* Hold Validation Message */}
      {!canHold && (
        <HoldValidationMessage
          message={
            toTypeCd === 'A'
              ? 'Cannot Hold Request: Start date is less than 1 day'
              : 'Cannot Hold Request: Start date is less than 15 days'
          }
        />
      )}

      {selectedStatus === STATUS_PARTIALLY_APPROVED && (
        <div className="pb-4">
          <h4 className="text-[#0D1C3D] text-base font-light leading-6 tracking-[0.16px]">
            To partially approve a time off request, keep the checkboxes
            selected for approved dates and deselect the checkboxes for denied
            dates.
          </h4>
        </div>
      )}
      {/* Date Selection Table - Only show when Partially Approved/Denied is selected */}
      {selectedStatus === STATUS_PARTIALLY_APPROVED && normalizedRows.length > 1 && (
          <div className="mb-6 pt-6 pb-6!">
            <Table
              columns={[
                { key: 'approved', label: 'Approved' },
                { key: 'date', label: 'Date' },
                { key: 'day', label: 'Day' },
                { key: 'times', label: 'Times' },
                { key: 'requestedHours', label: 'Hours' },
              ]}
              rows={normalizedRows.map((row) => ({
                id: row.id,
                approved: (
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={approvedDates.includes(row.id)}
                      onChange={() => handleDateToggle(row.id)}
                      className="w-4 h-4"
                      disabled={!!row.isOverlapped}
                      aria-disabled={!!row.isOverlapped}
                    />
                    {/* checkbox is disabled when row.isOverlapped is true; no extra label */}
                  </div>
                ),
                date: formatDateToMMDDYYYY(row.date),
                day: row.day,
                times: formatTimesDisplay(row.times),
                requestedHours: row.requestedHours,
              }))}
              showPagination={false}
            />
          </div>
        )}

      {/* Current Request Table is rendered elsewhere in the page. This form
          only manages the approval checkbox state and emits approved-hours
          data via `onApprovedChange` so the single Current Request table
          remains the source of truth. */}

      {/* Reason for Denial Dropdown - Always show when partially approved/denied is selected */}
      {selectedStatus === STATUS_PARTIALLY_APPROVED && (
        <>
          <div className="mb-6 pt-6 pb-6!" id="reason-for-denial">
            <h4 className="text-[#0D1C3D] text-base font-light leading-6 tracking-[0.16px] mb-4">
              Reason for Denial<span className="text-red-600">*</span>
            </h4>
            <Dropdown
              options={denialReasonOptions}
              selectedValue={selectedDenialReason}
              placeholder="Select a Reason for Denial"
              onSelect={(option) => {
                setSelectedDenialReason(option)
                setValidationError(null)
              }}
              minWidth="270px"
            />
            {validationError === 'reasonNotSelected' && (
              <p className="text-red-600 text-xs mt-1 flex items-center gap-1">
                <img
                  src="/assets/images/errorCircleSmall.svg"
                  alt="error"
                  className="inline w-4 h-4"
                />
                <span>Reason for Denial is required</span>
              </p>
            )}
          </div>
          {selectedDenialReason?.value === 'other' && (
            <div className="mb-6 pt-6 !pb-6">
              <label htmlFor="otherReason" className="block p3 -medium mb-1">
                Describe Other<span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                id="otherReason"
                className="w-full h-10 border rounded p-2 disabled:bg-gray-100 disabled:cursor-not-allowed"
                maxLength={100}
                value={otherDenialReason}
                onChange={(e) => {
                  setOtherDenialReason(e.target.value)
                  setValidationError(null)
                }}
              />
              {validationError === 'otherReasonNotFilled' && (
                <p className="text-red-600 text-xs mt-1 flex items-center gap-1">
                  <img
                    src="/assets/images/errorCircleSmall.svg"
                    alt="error"
                    className="inline w-4 h-4"
                  />
                  <span>Reason for Denial is required</span>
                </p>
              )}
            </div>
          )}
        </>
      )}

      {/* Approver Comments */}
      <div className="mb-6 pt-6 pb-6!">
        <label htmlFor="comments" className="block p3 -medium mb-1">
          Approver Comments
        </label>
        <textarea
          id="comments"
          className="w-full border rounded p-2 min-h-20 disabled:bg-gray-100 disabled:cursor-not-allowed"
          maxLength={1000}
          value={approverComments}
          onChange={(e) => setApproverComments(e.target.value)}
          disabled={isDisabledDueToDate || areBothDatesInPast}
        />
        <div className="text-left text-xs text-gray-500 mt-1">
          {1000 - approverComments.length} of 1000 characters left
        </div>
      </div>

      {/* Buttons */}
      {/* Buttons */}
      <div className="pb-8!">
        <ButtonGroup
          onCancel={onCancel}
          onSubmit={handleSubmit}
          submitDisabled={
            isDisabledDueToDate ||
            areBothDatesInPast ||
            approvedDates.length === 0 ||
            approvedDates.length ===
              normalizedRows.filter((row) => !row.isOverlapped).length
          }
        />
      </div>
    </div>
  )
}

export default PartiallyApprovedTimeOffForm
